/**
 * BlockPreviewRenderer Service
 *
 * Single source of truth for rendering strategy block previews.
 * Converts Firebase StrategyBlock entities into visual React components.
 *
 * Features:
 * - Renders block metadata (name, type, description)
 * - Supports all 10 blockTypes from schema
 * - Scalable previews (consistent with ScreenPreviewRenderer)
 * - Material-UI based rendering
 *
 * Usage:
 * ```typescript
 * import { blockPreviewRenderer } from './services/BlockPreviewRenderer';
 *
 * // Render template preview
 * const preview = blockPreviewRenderer.renderBlock(block, {
 *   scale: 1.0
 * });
 *
 * // Get all templates
 * const templates = blockPreviewRenderer.getTemplateBlocks();
 * ```
 *
 * Sprint: sprint-7
 * Task: T005.1.2
 * Requirement: FR-P5-005 - Template Panel shows blocks in blocks mode
 */

import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { StrategyBlock } from '@segmently/schema-models/strategy';
import { SCREEN_PREVIEW_SCALE } from '@modules/reactFlow/constants/screenPreviewConfig';
import {
  BLOCK_DIMENSIONS,
  BLOCK_LAYOUT,
} from '@modules/reactFlow/constants/blockDimensionConfig';
import { MOCK_TEMPLATE_BLOCKS } from './mockTemplateBlocks';

/**
 * Render options for block preview
 */
export interface RenderOptions {
  /** Scale factor (default 1.0) */
  scale?: number;
  /** Enable interactive elements */
  interactive?: boolean;
  /** Show block type badge */
  showTypeBadge?: boolean;
  /**
   * Whether the Rationale/Instructions body scrolls (react-flow `nowheel`).
   * On the canvas only the SELECTED node passes true — otherwise every hovered
   * node swallows the wheel and breaks zoom/pan (dev feedback 2026-08-05).
   * Default true for non-canvas consumers.
   */
  scrollableBody?: boolean;
}

/**
 * Block type colors for visual distinction
 */
export const BLOCK_TYPE_COLORS: Record<string, string> = {
  welcome: '#4CAF50',           // Green - welcoming
  user_goals: '#2196F3',        // Blue - goals
  user_data: '#9C27B0',         // Purple - data
  plan_paywall: '#FF9800',      // Orange - paywall
  history: '#795548',           // Brown - past
  motivation: '#E91E63',        // Pink - motivation
  environment: '#009688',       // Teal - environment
  habits: '#3F51B5',            // Indigo - habits
  strategy: '#F44336',          // Red - strategy
  custom: '#607D8B',            // Blue Grey - custom
};

/**
 * Block type icons for visual representation
 */
const BLOCK_TYPE_ICONS: Record<string, string> = {
  welcome: '👋',
  user_goals: '🎯',
  user_data: '📊',
  plan_paywall: '💳',
  history: '📜',
  motivation: '💪',
  environment: '🌍',
  habits: '🔄',
  strategy: '📋',
  custom: '⚙️',
};

/**
 * Leveled block instructions (C2: strategist-authored). The installed
 * schema-models type may predate the field, so it is read defensively.
 */
interface BlockInstructionEntry {
  level: 'mustHave' | 'should' | 'niceToHave' | 'forbidden';
  text: string;
}

const INSTRUCTION_GROUPS: Array<{ level: BlockInstructionEntry['level']; caption: string; paletteColor: string }> = [
  { level: 'mustHave', caption: 'MUST HAVE', paletteColor: 'error.main' },
  { level: 'should', caption: 'SHOULD', paletteColor: 'warning.main' },
  { level: 'niceToHave', caption: 'NICE TO HAVE', paletteColor: 'info.main' },
  { level: 'forbidden', caption: 'FORBIDDEN', paletteColor: 'error.main' },
];

const SECTION_LABEL_SX = {
  fontSize: '9.5px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: 0.5,
} as const;

function readBlockInstructions(block: StrategyBlock): BlockInstructionEntry[] {
  const raw = (block as { instructions?: unknown }).instructions;
  if (!Array.isArray(raw)) return [];
  const levels = new Set(INSTRUCTION_GROUPS.map((group) => group.level));
  return raw
    .filter((entry): entry is BlockInstructionEntry => Boolean(
      entry
      && typeof entry === 'object'
      && levels.has((entry as BlockInstructionEntry).level)
      && typeof (entry as BlockInstructionEntry).text === 'string'
      && (entry as BlockInstructionEntry).text.trim().length > 0,
    ))
    .slice(0, 40);
}

/**
 * Block Preview Renderer Service
 *
 * Renders Firebase StrategyBlock objects as React components
 */
class BlockPreviewRenderer {
  /**
   * Main rendering method
   *
   * Creates simplified block preview with icon, name, type, and description.
   *
   * @param block - Firebase StrategyBlock entity
   * @param options - Rendering options
   * @returns React element with block preview
   */
  renderBlock(block: StrategyBlock, options: RenderOptions = {}): React.ReactElement {
    const {
      scale = SCREEN_PREVIEW_SCALE.FULL_SIZE,
      interactive = false,
      showTypeBadge = true,
      scrollableBody = true,
    } = options;

    const blockType = block.blockType || 'custom';
    const color = BLOCK_TYPE_COLORS[blockType] || BLOCK_TYPE_COLORS.custom;
    const icon = BLOCK_TYPE_ICONS[blockType] || BLOCK_TYPE_ICONS.custom;

    return this.renderBlockContainer(block, scale, showTypeBadge, color, icon, scrollableBody);
  }

  /**
   * Get template blocks for BlockTemplatePanel
   *
   * @returns Array of mock StrategyBlock instances
   */
  getTemplateBlocks(): Partial<StrategyBlock>[] {
    return MOCK_TEMPLATE_BLOCKS;
  }

  /**
   * Block container with visual representation
   *
   * New layout (4:3 aspect ratio):
   * - Header: block name + type badge (40px)
   * - Content: description area (~276px)
   * - Footer: colored accent bar (3px)
   */
  private renderBlockContainer(
    block: StrategyBlock,
    scale: number,
    showTypeBadge: boolean,
    color: string,
    icon: string,
    scrollableBody: boolean = true
  ): React.ReactElement {
    const blockType = block.blockType || 'custom';
    const name = block.name || 'Unnamed Block';
    const description = block.description || '';

    const instructions = readBlockInstructions(block);
    const screensCount = Array.isArray((block as { screens?: unknown[] }).screens)
      ? (block as { screens?: unknown[] }).screens!.length
      : undefined;
    const firebaseId = block.id;

    return (
      <Box
        sx={{
          width: BLOCK_DIMENSIONS.WIDTH,
          height: BLOCK_DIMENSIONS.HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          borderLeft: `4px solid ${color}`,
          borderRadius: `${BLOCK_LAYOUT.BORDER_RADIUS}px`,
          backgroundColor: 'background.paper',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header: title + type chip, then one meta line (entityId · screens · copy-id) */}
        <Box sx={{ px: 1.75, pt: 1.25, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                flex: 1,
                fontSize: '15px',
                fontWeight: 500,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </Typography>
            {showTypeBadge && (
              <Box
                sx={{
                  fontSize: '9px',
                  fontWeight: 600,
                  color: color,
                  border: `1px solid ${color}`,
                  px: 1,
                  py: 0.25,
                  borderRadius: '10px',
                  textTransform: 'uppercase',
                  flexShrink: 0,
                }}
              >
                {blockType.replace(/_/g, ' ')}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            <Typography
              variant="caption"
              sx={{ fontSize: '10.5px', color: 'text.secondary', fontFamily: 'monospace' }}
            >
              {block.entityId}
            </Typography>
            {screensCount !== undefined && (
              <Typography variant="caption" sx={{ fontSize: '10.5px', color: 'text.secondary' }}>
                · {screensCount} screen{screensCount === 1 ? '' : 's'}
              </Typography>
            )}
            {firebaseId && (
              <Tooltip title={`Copy firebaseId: ${firebaseId}`} arrow>
                <IconButton
                  size="small"
                  sx={{ p: 0.25, color: 'text.secondary' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    void navigator.clipboard?.writeText(firebaseId);
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Body: Rationale + leveled Instructions. Scrolls ONLY when the node
            is selected (`scrollableBody`) — `nowheel` makes react-flow hand
            the wheel to this box, which on unselected nodes would swallow
            canvas zoom/pan on every hover (dev feedback 2026-08-05). */}
        <Box
          className={scrollableBody ? 'nowheel' : undefined}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: scrollableBody ? 'auto' : 'hidden',
            p: BLOCK_LAYOUT.CONTENT_PADDING / 8, // Convert to MUI spacing units
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'divider', borderRadius: 3 },
          }}
        >
          {!description && instructions.length === 0 && (
            <Typography
              sx={{ fontSize: '12px', color: 'text.disabled', fontStyle: 'italic', textAlign: 'center', mt: 2 }}
            >
              No description provided
            </Typography>
          )}
          {description && (
            <>
              <Typography sx={SECTION_LABEL_SX}>Rationale</Typography>
              <Typography sx={{ fontSize: '12px', color: 'text.primary', lineHeight: 1.45, mb: instructions.length > 0 ? 1.25 : 0 }}>
                {description}
              </Typography>
            </>
          )}
          {instructions.length > 0 && (
            <>
              <Typography sx={SECTION_LABEL_SX}>Instructions</Typography>
              {INSTRUCTION_GROUPS.map((group) => {
                const items = instructions.filter((entry) => entry.level === group.level);
                if (items.length === 0) return null;
                return (
                  <Box
                    key={group.level}
                    sx={{ borderLeft: 3, borderColor: group.paletteColor, pl: 1, mb: 1 }}
                  >
                    <Typography
                      sx={{ fontSize: '8.5px', fontWeight: 700, letterSpacing: '0.06em', color: group.paletteColor, mb: 0.25 }}
                    >
                      {group.caption}
                    </Typography>
                    {items.map((entry, index) => (
                      <Typography
                        key={`${group.level}-${index}`}
                        sx={{ fontSize: '11.5px', lineHeight: 1.4, color: 'text.primary' }}
                      >
                        {group.level === 'forbidden' ? '⃠ ' : '• '}{entry.text}
                      </Typography>
                    ))}
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </Box>
    );
  }
}

/**
 * Singleton instance
 */
export const blockPreviewRenderer = new BlockPreviewRenderer();

/**
 * Default export
 */
export default blockPreviewRenderer;
